export const INSTALL_DISMISS_KEY = 'lgl-m-install-dismissed';
export const FIELD_APK_HREF = '/api/downloads/LGL-Portal.apk';
export const FIELD_APK_NAME = 'LGL-Portal.apk';
export const TEAM_APK_URL = `https://portal.laframboise.net${FIELD_APK_HREF}`;
export const LEGACY_FIELD_APK_HREF = '/api/downloads/LGL-Field-Progress.apk';
export const LEGACY_FIELD_APK_NAME = 'LGL-Field-Progress.apk';
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

export const isNativeFieldApp = (win = typeof window === 'undefined' ? undefined : window) =>
  Boolean(win?.Capacitor?.isNativePlatform?.());

export const isStandaloneApp = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return false;
  const standalone = Boolean(win.matchMedia?.('(display-mode: standalone)')?.matches);
  const iosHome = win.navigator?.standalone === true;
  const capacitor = Boolean(win.Capacitor?.isNativePlatform?.());
  return standalone || iosHome || capacitor;
};

/** Classic UA check (Android desktop-site often still contains "Android"). */
export const uaLooksLikePhoneOrTablet = (ua = '') =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(String(ua));

/** Pocket phones. Android tablets usually omit "Mobile"; iPad is its own token. */
export const uaLooksLikePhone = (ua = '') => {
  const value = String(ua);
  if (/iPhone|iPod/i.test(value)) return true;
  return /Android/i.test(value) && /Mobile/i.test(value);
};

export const uaLooksLikeTablet = (ua = '') => {
  const value = String(ua);
  if (/iPad|Tablet/i.test(value)) return true;
  return /Android/i.test(value) && !/Mobile/i.test(value);
};

/**
 * Cell phone only — tablets and desktops stay on the regular portal.
 * Chrome Client Hints survive "Desktop site" on a phone.
 */
export const isPhoneDevice = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return false;
  const nav = win.navigator || {};
  const ua = String(nav.userAgent || '');

  // Tablet UA wins over Client Hints (Playwright / some WebViews mark iPad as mobile).
  if (uaLooksLikeTablet(ua)) return false;
  if (uaLooksLikePhone(ua)) return true;
  if (nav.userAgentData?.mobile === true) return true;
  // iPadOS 13+ reports Macintosh.
  if (/Macintosh/i.test(ua) && Number(nav.maxTouchPoints || 0) > 1) return false;

  const touch = Number(nav.maxTouchPoints || 0) > 0 || ('ontouchstart' in win);
  const width = Math.min(
    Number(win.screen?.width || Infinity),
    Number(win.innerWidth || Infinity),
  );
  const pocket = Number.isFinite(width) && width > 0 && width <= 500;
  const canHover = Boolean(win.matchMedia?.('(hover: hover)')?.matches);
  const finePointer = Boolean(win.matchMedia?.('(pointer: fine)')?.matches);
  return Boolean(touch && pocket && !(canHover && finePointer));
};

/**
 * Real phone/tablet — including Chrome "Desktop site" and iPadOS pretending to be Mac.
 * Laptops (even touchscreen) stay false when they have fine pointer + hover + wide screen.
 */
export const isPhoneOrTabletDevice = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return false;
  const nav = win.navigator || {};
  const ua = String(nav.userAgent || '');

  // Client Hints (Chrome): survives "Desktop site" better than UA alone.
  if (nav.userAgentData?.mobile === true) return true;

  if (uaLooksLikePhoneOrTablet(ua)) return true;

  // iPadOS 13+ often reports Macintosh — detect by touch points.
  if (/Macintosh/i.test(ua) && Number(nav.maxTouchPoints || 0) > 1) return true;

  const touch = Number(nav.maxTouchPoints || 0) > 0 || ('ontouchstart' in win);
  const width = Math.min(
    Number(win.screen?.width || Infinity),
    Number(win.innerWidth || Infinity),
  );
  const narrow = Number.isFinite(width) && width > 0 && width <= 920;
  const canHover = Boolean(win.matchMedia?.('(hover: hover)')?.matches);
  const finePointer = Boolean(win.matchMedia?.('(pointer: fine)')?.matches);

  // Touch + narrow + not a hover/fine-pointer laptop → phone or tablet in desktop mode.
  if (touch && narrow && !(canHover && finePointer)) return true;

  return false;
};

/** @deprecated use isPhoneOrTabletDevice — kept for older call sites / tests */
export const isPhoneViewport = (win = typeof window === 'undefined' ? undefined : window) =>
  isPhoneOrTabletDevice(win);

export const isAndroidPhone = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return false;
  const nav = win.navigator || {};
  const ua = String(nav.userAgent || '');
  if (/Android/i.test(ua)) return true;
  // Client Hints platform when UA is spoofed as desktop.
  const platform = String(nav.userAgentData?.platform || '');
  return /Android/i.test(platform);
};

export const isIosDevice = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return false;
  const nav = win.navigator || {};
  const ua = String(nav.userAgent || '');
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (/Macintosh/i.test(ua) && Number(nav.maxTouchPoints || 0) > 1) return true;
  const platform = String(nav.userAgentData?.platform || '');
  return /iOS|iPadOS/i.test(platform);
};

/** ?install=1 or #install forces the sheet (also clears dismiss). */
export const installForcedByUrl = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win?.location) return false;
  try {
    const q = new URLSearchParams(win.location.search || '');
    if (q.get('install') === '1' || q.get('install') === 'true') return true;
  } catch { /* ignore */ }
  return String(win.location.hash || '').toLowerCase().includes('install');
};

export const clearInstallDismissed = (win = typeof window === 'undefined' ? undefined : window) => {
  try { win?.localStorage?.removeItem(INSTALL_DISMISS_KEY); } catch { /* ignore */ }
};

/**
 * Home download CTA: phones/tablets only.
 * Laptops/desktops never see it — even if the window is narrow.
 * Already-installed home-screen / Capacitor apps skip it too.
 */
export const shouldShowHomeDownloadCta = (win = typeof window === 'undefined' ? undefined : window) => {
  if (!win) return false;
  if (isStandaloneApp(win) || isNativeFieldApp(win)) return false;
  return isPhoneOrTabletDevice(win);
};

export const shouldShowInstallPrompt = ({
  standalone,
  phone,
  dismissedAt,
  force = false,
  now = Date.now(),
} = {}) => {
  if (standalone) return false;
  if (force) return true;
  if (!phone) return false;
  if (!dismissedAt) return true;
  return now - Number(dismissedAt) > DISMISS_MS;
};

export const readDismissedAt = () => {
  try {
    return Number(localStorage.getItem(INSTALL_DISMISS_KEY) || 0) || 0;
  } catch {
    return 0;
  }
};

export const rememberInstallDismissed = () => {
  try { localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
};

/** Prefer API download (correct Android MIME); fall back to static /downloads. */
export const resolveFieldApkHref = async (fetchImpl = fetch) => {
  const candidates = [
    FIELD_APK_HREF,
    `/downloads/${FIELD_APK_NAME}`,
    LEGACY_FIELD_APK_HREF,
    `/downloads/${LEGACY_FIELD_APK_NAME}`,
  ];
  for (const href of candidates) {
    try {
      const res = await fetchImpl(href, { method: 'HEAD' });
      const type = String(res.headers?.get?.('content-type') || '');
      if (res.ok && !type.includes('text/html')) return href;
    } catch {
      /* try next */
    }
  }
  return null;
};
